import React, { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  ArrowRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface QuizComponentProps {
  quiz: any;
  courseId: string;
  moduleId: string;
  onComplete: (score: number, passed: boolean) => void;
  onClose: () => void;
}

interface QuizAttempt {
  startTime: Date;
  answers: Record<string, string>;
  timeSpent: number;
  currentQuestion: number;
}

// Countdown display component that forces re-renders
const CooldownTimer: React.FC<{ endTime: Date; className?: string }> = ({ endTime, className }) => {
  const [secondsLeft, setSecondsLeft] = useState(0);
  
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const timeLeft = Math.max(0, endTime.getTime() - now.getTime());
      const seconds = Math.ceil(timeLeft / 1000);
      setSecondsLeft(seconds);
      
      if (seconds <= 0) {
        return false; // Stop the timer
      }
      return true;
    };
    
    // Initial update
    if (!updateTimer()) return;
    
    const interval = setInterval(() => {
      if (!updateTimer()) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [endTime]);
  
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  
  return (
    <span className={className || "text-orange-700 text-lg font-mono font-bold"}>
      {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  );
};

// Button text component for retake button
const RetakeButtonText: React.FC<{ cooldownEndTime: Date | null }> = ({ cooldownEndTime }) => {
  if (!cooldownEndTime) return <>Retake Quiz</>;
  
  return (
    <>
      Retake (<CooldownTimer endTime={cooldownEndTime} className="" />)
    </>
  );
};

// Progress bar component for cooldown
const CooldownProgressBar: React.FC<{ endTime: Date; totalDuration: number }> = ({ endTime, totalDuration }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const updateProgress = () => {
      const now = new Date();
      const timeLeft = Math.max(0, endTime.getTime() - now.getTime());
      const elapsed = totalDuration - (timeLeft / 1000);
      const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
      setProgress(progressPercent);
      
      return timeLeft > 0;
    };
    
    updateProgress();
    const interval = setInterval(() => {
      if (!updateProgress()) {
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [endTime, totalDuration]);
  
  return (
    <div className="w-full bg-orange-200 rounded-full h-2">
      <div
        className="bg-orange-600 h-2 rounded-full transition-all duration-1000"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

const QuizComponent: React.FC<QuizComponentProps> = ({
  quiz,
  courseId,
  moduleId,
  onComplete,
  onClose
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(quiz?.timeLimit ? quiz.timeLimit * 60 : 0);
  const [quizAttempt, setQuizAttempt] = useState<QuizAttempt | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showExplanations, setShowExplanations] = useState<Record<string, boolean>>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [cooldownEndTime, setCooldownEndTime] = useState<Date | null>(null);
  const [timeUntilRetake, setTimeUntilRetake] = useState<number>(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [suspiciousActivity, setSuspiciousActivity] = useState(false);

  // Initialize quiz attempt
  useEffect(() => {
    const attempt: QuizAttempt = {
      startTime: new Date(),
      answers: {},
      timeSpent: 0,
      currentQuestion: 0
    };
    setQuizAttempt(attempt);

    // Check for existing cooldown in multiple storages
    const checkCooldown = () => {
      if (!quiz?.id) return false;
      const savedCooldown = localStorage.getItem(`quiz_cooldown_${quiz.id}`);
      const sessionCooldown = sessionStorage.getItem(`quiz_cooldown_${quiz.id}`);
      const browserCooldown = localStorage.getItem(`quiz_global_cooldown_${quiz.id}`);
      
      // Use the latest cooldown time from any storage
      const cooldownTimes = [savedCooldown, sessionCooldown, browserCooldown]
        .filter(Boolean)
        .map(time => new Date(time!));
      
      if (cooldownTimes.length > 0) {
        const latestCooldown = new Date(Math.max(...cooldownTimes.map(d => d.getTime())));
        if (latestCooldown > new Date()) {
          setCooldownEndTime(latestCooldown);
          return true;
        } else {
          // Clear all expired cooldowns
          localStorage.removeItem(`quiz_cooldown_${quiz.id}`);
          sessionStorage.removeItem(`quiz_cooldown_${quiz.id}`);
          localStorage.removeItem(`quiz_global_cooldown_${quiz.id}`);
        }
      }
      return false;
    };

    checkCooldown();

    // Check every second for tampered cooldowns
    const cooldownCheck = setInterval(checkCooldown, 1000);

    return () => clearInterval(cooldownCheck);
  }, [quiz?.id]);

  // Window focus tracking for anti-cheating
  useEffect(() => {
    if (showResults || quizCompleted) return;

    const handleFocus = () => {
      setWindowFocused(true);
    };

    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        setSuspiciousActivity(true);
        toast.error('⚠️ Multiple tab switches detected. This may affect your quiz submission.');
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
        // Don't increment tabSwitches here as handleBlur already handles it
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showResults, quizCompleted, tabSwitches]);

  // Cooldown timer - runs every second to update the countdown
  useEffect(() => {
    if (!cooldownEndTime) {
      setTimeUntilRetake(0);
      return;
    }

    // Initial calculation
    const now = new Date();
    const initialTimeLeft = Math.max(0, cooldownEndTime.getTime() - now.getTime());
    setTimeUntilRetake(Math.ceil(initialTimeLeft / 1000));

    const interval = setInterval(() => {
      const currentTime = new Date();
      const timeLeft = Math.max(0, cooldownEndTime.getTime() - currentTime.getTime());
      const secondsLeft = Math.ceil(timeLeft / 1000);
      
      console.log('Cooldown update:', secondsLeft); // Debug log
      setTimeUntilRetake(secondsLeft);
      
      // Show warning when 1 minute left
      if (secondsLeft === 60) {
        toast('⏰ 1 minute remaining until you can retake the quiz', {
          icon: '⏰',
          duration: 3000,
        });
      }
      
      if (timeLeft <= 0) {
        setCooldownEndTime(null);
        setTimeUntilRetake(0);
        if (quiz?.id) {
          localStorage.removeItem(`quiz_cooldown_${quiz.id}`);
          sessionStorage.removeItem(`quiz_cooldown_${quiz.id}`);
          localStorage.removeItem(`quiz_global_cooldown_${quiz.id}`);
        }
        toast.success('🎉 Cooldown expired! You can now retake the quiz.');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownEndTime, quiz?.id]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0 || showResults) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auto-submit when time runs out
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, showResults]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = () => {
    if (quiz?.questions && currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to submit quiz');
        return;
      }

      const timeSpent = quizAttempt ? 
        Math.floor((new Date().getTime() - quizAttempt.startTime.getTime()) / 1000) : 0;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/quiz/${courseId}/${quiz?.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            answers,
            timeSpent,
            moduleId,
            trackingData: {
              tabSwitches,
              suspiciousActivity,
              focusLost: !windowFocused,
              timeSpentFocused: timeSpent, // Could be refined to track only focused time
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            }
          })
        }
      );

      const result = await response.json();
      
      if (response.ok && result.success) {
        setResults(result.data);
        setShowResults(true);
        setQuizCompleted(true);
        onComplete(result.data.score, result.data.passed);
        
        // Set 30-minute cooldown for retakes if failed
        if (!result.data.passed && quiz?.id) {
          const cooldownEnd = new Date(Date.now() + 30 * 60 * 1000); // 30 minutes
          setCooldownEndTime(cooldownEnd);
          // Store in multiple locations to prevent easy bypass
          localStorage.setItem(`quiz_cooldown_${quiz.id}`, cooldownEnd.toISOString());
          sessionStorage.setItem(`quiz_cooldown_${quiz.id}`, cooldownEnd.toISOString());
          localStorage.setItem(`quiz_global_cooldown_${quiz.id}`, cooldownEnd.toISOString());
          
          // Also store a hash to verify integrity
          const cooldownHash = btoa(`${quiz.id}-${cooldownEnd.getTime()}-${navigator.userAgent}`);
          localStorage.setItem(`quiz_cooldown_hash_${quiz.id}`, cooldownHash);
          
          toast.error(`You scored ${result.data.score}%. You need ${quiz?.passingScore || 70}% to pass. Please wait 30 minutes before retrying.`);
        } else {
          toast.success(`Congratulations! You passed with ${result.data.score}%`);
        }

        // Show all explanations after completion
        const allExplanations: Record<string, boolean> = {};
        quiz?.questions?.forEach((q: any) => {
          if (q.explanation) {
            allExplanations[q.id] = true;
          }
        });
        setShowExplanations(allExplanations);
      } else {
        toast.error(result.message || 'Failed to submit quiz');
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = () => {
    // Check if all required questions are answered
    return quiz?.questions?.every((question: any) => answers[question.id]) || false;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  if (showResults && results) {
    return (
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-96 overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Quiz Results</h2>
        </div>
        
        <div className="p-6">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              results.passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {results.passed ? (
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              ) : (
                <XCircleIcon className="h-8 w-8 text-red-600" />
              )}
            </div>
            <h3 className={`text-2xl font-bold mb-2 ${
              results.passed ? 'text-green-600' : 'text-red-600'
            }`}>
              {results.passed ? 'Congratulations!' : 'Keep Trying!'}
            </h3>
            <p className="text-gray-600 mb-4">
              You scored {results.score}% ({results.correctAnswers} out of {results.totalQuestions} correct)
            </p>
            <p className="text-sm text-gray-500">
              Passing score: {quiz?.passingScore || 70}%
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900">{results.score}%</div>
              <div className="text-sm text-gray-600">Your Score</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900">{results.correctAnswers}</div>
              <div className="text-sm text-gray-600">Correct Answers</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900">{formatTime(results.timeSpent || 0)}</div>
              <div className="text-sm text-gray-600">Time Spent</div>
            </div>
          </div>

          {!results.passed && (quiz?.attempts || 1) > 1 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 text-sm">
                You can retake this quiz. You have used 1 of {quiz?.attempts || 1} attempts.
              </p>
{cooldownEndTime && timeUntilRetake > 0 && (
                <div key={`cooldown-${timeUntilRetake}`} className="bg-orange-50 border border-orange-200 rounded p-3 mt-2">
                  <div className="flex items-center justify-between">
                    <p className="text-orange-800 text-sm font-medium">
                      ⏱️ Cooldown Active
                    </p>
                    <CooldownTimer endTime={cooldownEndTime} />
                  </div>
<div className="mt-2">
                    <CooldownProgressBar endTime={cooldownEndTime} totalDuration={30 * 60} />
                  </div>
                  <p className="text-orange-600 text-xs mt-1">
                    Time remaining until you can retake this quiz
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-4">
{!results.passed && (quiz?.attempts || 1) > 1 && (
              <button
                key={`retake-btn-${timeUntilRetake}`}
                onClick={() => {
                  setShowResults(false);
                  setCurrentQuestion(0);
                  setAnswers({});
                  setQuizCompleted(false);
                  setShowExplanations({});
                  setTimeLeft(quiz?.timeLimit ? quiz.timeLimit * 60 : 0);
                  setTabSwitches(0);
                  setSuspiciousActivity(false);
                }}
                disabled={!!(cooldownEndTime && timeUntilRetake > 0)}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  cooldownEndTime && timeUntilRetake > 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
<RetakeButtonText cooldownEndTime={cooldownEndTime && timeUntilRetake > 0 ? cooldownEndTime : null} />
              </button>
            )}
            <button
              onClick={() => setShowResults(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Review Questions
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Add safety checks for quiz data
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
        <div className="text-center">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Quiz Not Available</h3>
          <p className="text-gray-600 mb-4">This quiz data is not properly configured.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];

  return (
    <div className="bg-white w-full h-full">
      {/* Quiz Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{quiz?.title || 'Quiz'}</h2>
            <p className="text-gray-600 mt-1">{quiz?.description || 'Take this quiz to test your knowledge'}</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Focus indicator */}
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            {/* Tab switches warning */}
            {tabSwitches > 0 && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                ⚠️ {tabSwitches} tab switch{tabSwitches !== 1 ? 'es' : ''}
              </div>
            )}
            
            {quiz?.timeLimit && (
              <div className="flex items-center text-sm">
                <ClockIcon className="h-4 w-4 mr-1 text-gray-500" />
                <span className={timeLeft < 300 ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            )}
            <div className="text-sm text-gray-600">
              Question {currentQuestion + 1} of {quiz?.questions?.length || 0}
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{getAnsweredCount()} / {quiz?.questions?.length || 0} answered</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${quiz?.questions?.length ? ((currentQuestion + 1) / quiz.questions.length) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {question?.question || 'Question not available'}
          </h3>

          {question?.type === 'multiple-choice' && question?.options && (
            <div className="space-y-3">
              {question.options.map((option: string, index: number) => (
                <label key={index} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={question?.id || `question-${currentQuestion}`}
                    value={option}
                    checked={question?.id ? answers[question.id] === option : false}
                    onChange={(e) => question?.id && handleAnswerChange(question.id, e.target.value)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-3 text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question?.type === 'true-false' && (
            <div className="space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={question?.id || `question-${currentQuestion}`}
                  value="true"
                  checked={question?.id ? answers[question.id] === 'true' : false}
                  onChange={(e) => question?.id && handleAnswerChange(question.id, e.target.value)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-3 text-gray-700">True</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={question?.id || `question-${currentQuestion}`}
                  value="false"
                  checked={question?.id ? answers[question.id] === 'false' : false}
                  onChange={(e) => question?.id && handleAnswerChange(question.id, e.target.value)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-3 text-gray-700">False</span>
              </label>
            </div>
          )}

          {question?.type === 'short-answer' && (
            <textarea
              rows={4}
              placeholder="Enter your answer..."
              value={question?.id ? answers[question.id] || '' : ''}
              onChange={(e) => question?.id && handleAnswerChange(question.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          )}
        </div>

        {/* Question explanation hint - only show after quiz completion */}
        {question?.explanation && quizCompleted && question?.id && showExplanations[question.id] && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex">
              <LightBulbIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Explanation</h4>
                <p className="text-sm text-blue-800 mt-1">{question?.explanation}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="p-6 border-t border-gray-200 flex justify-between items-center">
        <button
          onClick={previousQuestion}
          disabled={currentQuestion === 0}
          className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
            currentQuestion === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Previous
        </button>

        <div className="flex space-x-2">
          {currentQuestion === (quiz?.questions?.length || 1) - 1 ? (
            <button
              onClick={handleSubmitQuiz}
              disabled={!canSubmit() || submitting}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                canSubmit() && !submitting
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </button>
          ) : (
            <button
              onClick={nextQuestion}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Next
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QuizComponent;